/**
 * SECCIÓN F2 — Sub-pestaña "Estatus de Almacenes" (Gestión de Residuos).
 * G2 — Panel lateral fijo reutilizable WarehouseCapacityPanel.
 *
 * Componente integrado: WarehouseStatusPanel (sub-pestaña) +
 * WarehouseCapacityPanel exportado para Citas de Almacén.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { Download, Edit2, Save, X, RefreshCw, AlertTriangle, Loader2 } from "lucide-react";

const TYPE_META = {
  RP:  { color: "#FF6D00", label: "RP"  },
  RME: { color: "#1565C0", label: "RME" },
  RSU: { color: "#2E7D32", label: "RSU" },
};

const STATUS_BADGE = {
  green:  { label: "✓ DISPONIBLE", bg: "rgba(0,200,83,0.18)",  fg: "#00C853" },
  yellow: { label: "⚠ ATENCIÓN",   bg: "rgba(255,214,0,0.22)", fg: "#B07A00" },
  red:    { label: "🔴 LLENO",     bg: "rgba(213,0,0,0.20)",   fg: "#D50000" },
};

function ProgressBar({ pct, color = "#1565C0" }) {
  const safe = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div style={{ position: "relative", background: "var(--bg-2)", height: 8, borderRadius: 4, overflow: "hidden", border: "1px solid var(--border-2)" }}>
      <div style={{ width: `${safe}%`, height: "100%", background: color, transition: "width 200ms" }} />
    </div>
  );
}

function StatusBadge({ color }) {
  const s = STATUS_BADGE[color] || STATUS_BADGE.green;
  return (
    <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.fg}55`,
                   padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────
// F2 — Sub-pestaña principal
// ─────────────────────────────────────────────────────────────────
export default function WarehouseStatusPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [forms, setForms] = useState({});
  const [saving, setSaving] = useState({});
  const [toast, setToast] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/warehouse-status");
      setItems(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  function startEdit(d) {
    setForms({ ...forms, [d.warehouse_type]: { ...d } });
    setEditing({ ...editing, [d.warehouse_type]: true });
  }

  function cancelEdit(wt) {
    setEditing({ ...editing, [wt]: false });
  }

  async function save(wt) {
    setSaving({ ...saving, [wt]: true });
    try {
      const payload = forms[wt] || {};
      await api.put(`/warehouse-status/${wt}`, payload);
      setToast(`Almacén ${wt} actualizado ✓`);
      setEditing({ ...editing, [wt]: false });
      await reload();
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      alert("Error guardando: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving({ ...saving, [wt]: false });
    }
  }

  async function downloadPdf() {
    try {
      const res = await api.post("/warehouse-status/report/pdf", {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "estatus_almacenes.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setToast("Reporte PDF descargado ✓");
      setTimeout(() => setToast(""), 2500);
    } catch (e) {
      alert("Error PDF: " + (e?.response?.data?.detail || e.message));
    }
  }

  if (loading) return <div className="card text-dim" data-testid="whs-loading">Cargando estatus de almacenes…</div>;

  return (
    <div data-testid="warehouse-status-panel" className="space-y-3">
      <div className="flex justify-end mb-2">
        <button className="btn" onClick={downloadPdf} data-testid="whs-report-pdf"><Download size={14}/> Reporte PDF</button>
      </div>
      {toast && (
        <div className="card" style={{ background: "rgba(0,200,83,0.10)", borderColor: "#00C853" }} data-testid="whs-toast">{toast}</div>
      )}

      <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(310px, 1fr))" }}>
        {items.map((d) => {
          const wt = d.warehouse_type;
          const meta = TYPE_META[wt] || TYPE_META.RP;
          const editMode = !!editing[wt];
          const f = forms[wt] || d;
          const setF = (patch) => setForms({ ...forms, [wt]: { ...f, ...patch } });
          return (
            <div key={wt} className="card" style={{ borderTop: `3px solid ${meta.color}` }} data-testid={`whs-card-${wt}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <span style={{ background: meta.color, color: "white", padding: "2px 8px", borderRadius: 4, fontWeight: 600, fontSize: 13 }}>{meta.label}</span>
                  <StatusBadge color={d.status_color} />
                </div>
                {editMode ? (
                  <div className="flex gap-1">
                    <button className="btn btn-sm" onClick={() => cancelEdit(wt)} disabled={saving[wt]}><X size={12}/></button>
                    <button className="btn btn-sm btn-primary" onClick={() => save(wt)} disabled={saving[wt]} data-testid={`whs-save-${wt}`}>
                      {saving[wt] ? <Loader2 size={12} className="spin"/> : <Save size={12}/>} Guardar
                    </button>
                  </div>
                ) : (
                  <button className="btn btn-sm" onClick={() => startEdit(d)} data-testid={`whs-edit-${wt}`}><Edit2 size={12}/> Editar</button>
                )}
              </div>

              <div className="text-xs text-dim">Responsable / Proveedor</div>
              {editMode
                ? <input className="input" value={f.responsible_name || ""} onChange={(e) => setF({ responsible_name: e.target.value })} data-testid={`whs-resp-${wt}`}/>
                : <div className="mb-2">{d.responsible_name || <span className="text-dim">Sin asignar</span>}</div>}

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <div className="text-xs text-dim">Capacidad Total</div>
                  {editMode
                    ? <div className="flex gap-1">
                        <input className="input" type="number" value={f.total_capacity_volume ?? 0} onChange={(e) => setF({ total_capacity_volume: parseFloat(e.target.value || 0) })} data-testid={`whs-tcap-${wt}`}/>
                        <select className="input" value={f.volume_unit || "m³"} onChange={(e) => setF({ volume_unit: e.target.value })}>
                          {["m³", "kg", "ton"].map((u) => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                    : <div>{d.total_capacity_volume} {d.volume_unit}</div>}
                </div>
                <div>
                  <div className="text-xs text-dim">Volumen Actual</div>
                  {editMode
                    ? <input className="input" type="number" value={f.current_volume ?? 0} onChange={(e) => setF({ current_volume: parseFloat(e.target.value || 0) })}/>
                    : <div>{d.current_volume} {d.volume_unit}</div>}
                </div>
              </div>

              <div className="mt-2">
                <ProgressBar pct={d.occupied_volume_pct} color={STATUS_BADGE[d.status_color]?.fg || "#1565C0"} />
                <div className="text-xs text-dim mt-1">{d.occupied_volume_pct}% ocupado · {d.available_volume.toFixed(1)} {d.volume_unit} disponibles</div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <div className="text-xs text-dim">Superficie Total</div>
                  {editMode
                    ? <input className="input" type="number" value={f.total_surface_m2 ?? 0} onChange={(e) => setF({ total_surface_m2: parseFloat(e.target.value || 0) })}/>
                    : <div>{d.total_surface_m2} m²</div>}
                </div>
                <div>
                  <div className="text-xs text-dim">Superficie Disponible</div>
                  {editMode
                    ? <input className="input" type="number" value={f.available_surface_m2 ?? 0} onChange={(e) => setF({ available_surface_m2: parseFloat(e.target.value || 0) })}/>
                    : <div>{d.available_surface_m2} m²</div>}
                </div>
              </div>
              <div className="mt-2">
                <ProgressBar pct={d.occupied_surface_pct} color={STATUS_BADGE[d.status_color]?.fg || "#1565C0"} />
                <div className="text-xs text-dim mt-1">{d.occupied_surface_pct}% ocupada</div>
              </div>

              <div className="grid grid-cols-2 gap-2 mt-2">
                <div>
                  <div className="text-xs text-dim">YTD Acumulado</div>
                  {editMode
                    ? <div className="flex gap-1">
                        <input className="input" type="number" value={f.ytd_accumulated ?? 0} onChange={(e) => setF({ ytd_accumulated: parseFloat(e.target.value || 0) })}/>
                        <select className="input" value={f.ytd_unit || "m³"} onChange={(e) => setF({ ytd_unit: e.target.value })}>
                          {["m³", "kg", "ton"].map((u) => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                    : <div>{d.ytd_accumulated} {d.ytd_unit}</div>}
                  <div className="text-xs text-dim">Volumen total administrado en el año</div>
                </div>
                <div>
                  <div className="text-xs text-dim">Nivel de alerta</div>
                  {editMode
                    ? <input className="input" type="number" min={0} max={100} value={f.alert_threshold_pct ?? 80} onChange={(e) => setF({ alert_threshold_pct: parseFloat(e.target.value || 0) })}/>
                    : <div>{d.alert_threshold_pct}%</div>}
                </div>
              </div>

              <div className="mt-2">
                <div className="text-xs text-dim">Tipo de residuo predominante</div>
                {editMode
                  ? <input className="input" value={f.dominant_waste_type || ""} onChange={(e) => setF({ dominant_waste_type: e.target.value })}/>
                  : <div>{d.dominant_waste_type || <span className="text-dim">—</span>}</div>}
              </div>

              <div className="mt-2">
                <div className="text-xs text-dim">Notas / Observaciones</div>
                {editMode
                  ? <textarea className="input" rows={3} value={f.notes || ""} onChange={(e) => setF({ notes: e.target.value })}/>
                  : <div className="text-sm">{d.notes || <span className="text-dim">—</span>}</div>}
              </div>

              <div className="text-xs text-dim mt-2" style={{ borderTop: "1px solid var(--border-2)", paddingTop: 6 }}>
                {d.updated_at
                  ? <>Última actualización: {new Date(d.updated_at).toLocaleString("es-MX")} {d.updated_by ? `por ${d.updated_by}` : ""}</>
                  : "Sin datos registrados"}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
